/**
 * Keeps in-flight index statuses honest without refetching the listing.
 *
 * Refetching is off the table by construction: `refreshData()` collapses the
 * scroll-accumulated buffer back to page 1. So this polls ONLY the ids that
 * are currently `pending`/`indexing`, and hands the fresh map to the caller,
 * who patches rows in place. No in-flight rows → no timer at all; the loop
 * is a recursive setTimeout so a slow response can never stack requests.
 */

import { useEffect, useMemo, useRef } from 'react';

import { useGetRequest } from '@/shared/hooks/requests.js';
import { FILE_INDEX_STATUS } from '@/shared/utils/urls.js';

export const POLL_INTERVAL_MS = 5000;
const MAX_IDS = 100;

const IN_FLIGHT = new Set(['pending', 'indexing']);

export function useIndexStatusPolling({ items, enabled, onStatuses }) {
  const statusRequest = useGetRequest({ showError: false });
  const timer = useRef(null);
  const callbacks = useRef({ onStatuses });
  callbacks.current.onStatuses = onStatuses;

  const inFlightIds = useMemo(() => {
    if (!enabled) return [];
    return items
      .filter(
        (item) =>
          item.type !== 'folder' &&
          IN_FLIGHT.has(item.indexStatus || item.index_status),
      )
      .map((item) => item.id)
      .slice(0, MAX_IDS);
  }, [items, enabled]);

  // Key on the SET of ids, not the array identity — items is rebuilt every
  // render and an identity dep would tear the timer down each time.
  const idsKey = inFlightIds.join(',');

  useEffect(() => {
    if (!idsKey) return undefined;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      // A hidden tab keeps its timer but skips the request — indexing
      // continues server-side and the next visible tick catches up.
      if (!document.hidden) {
        const { response, success } = await statusRequest.request({
          url: FILE_INDEX_STATUS,
          params: { ids: idsKey },
        });
        if (cancelled) return;
        if (success && response?.statuses) {
          callbacks.current.onStatuses(response.statuses);
        }
      }
      timer.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);
}
